// app/javascript/utils/email_parser.js
// Parses .eml (RFC 822) files and extracts meaningful content

export class EmailParser {
  constructor(rawEmail) {
    this.rawEmail = rawEmail;
    this.headers = {};
    this.body = { plain: null, html: null };
    this.parse();
  }

  parse() {
    const lines = this.rawEmail.split(/\r?\n/);
    let headerSection = true;
    let currentHeader = null;
    let headerValue = "";
    let bodyContent = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (headerSection) {
        // Empty line marks end of headers
        if (line === "") {
          // Save last header if exists
          if (currentHeader) {
            this.headers[currentHeader.toLowerCase()] = headerValue.trim();
          }
          headerSection = false;
          bodyContent = lines.slice(i + 1);
          break;
        }

        // Continuation of previous header (starts with whitespace)
        if (/^\s+/.test(line) && currentHeader) {
          headerValue += " " + line.trim();
          continue;
        }

        // New header
        const headerMatch = line.match(/^([^:]+):\s*(.*)$/);
        if (headerMatch) {
          // Save previous header
          if (currentHeader) {
            this.headers[currentHeader.toLowerCase()] = headerValue.trim();
          }
          currentHeader = headerMatch[1];
          headerValue = headerMatch[2];
        }
      }
    }

    // Parse body
    this.parseBody(bodyContent.join("\n"));
  }

  parseBody(bodyText) {
    const contentType = this.headers["content-type"] || "";

    if (contentType.includes("multipart/")) {
      this.parseMultipart(bodyText, contentType);
    } else if (contentType.includes("text/html")) {
      this.body.html = this.decodeContent(bodyText, this.headers["content-transfer-encoding"]);
    } else {
      this.body.plain = this.decodeContent(bodyText, this.headers["content-transfer-encoding"]);
    }
  }

  parseMultipart(bodyText, contentType) {
    // Extract boundary
    const boundaryMatch = contentType.match(/boundary="?([^";\s]+)"?/);
    if (!boundaryMatch) return;

    const boundary = boundaryMatch[1];
    const parts = bodyText.split(new RegExp(`--${this.escapeRegex(boundary)}`));

    for (const part of parts) {
      if (part.trim() === "" || part.trim() === "--") continue;

      const partLines = part.split(/\r?\n/);
      let partHeaders = {};
      let partBody = [];
      let inHeaders = true;

      // Skip leading empty lines (there's usually a newline after the boundary marker)
      let startLine = 0;
      while (startLine < partLines.length && partLines[startLine] === "") {
        startLine++;
      }

      for (let i = startLine; i < partLines.length; i++) {
        const line = partLines[i];

        if (inHeaders) {
          if (line === "") {
            inHeaders = false;
            partBody = partLines.slice(i + 1);
            break;
          }

          const headerMatch = line.match(/^([^:]+):\s*(.*)$/);
          if (headerMatch) {
            partHeaders[headerMatch[1].toLowerCase()] = headerMatch[2];
          }
        }
      }

      const partContentType = partHeaders["content-type"] || "";
      const partEncoding = partHeaders["content-transfer-encoding"] || "";
      const partContent = partBody.join("\n");

      if (partContentType.includes("multipart/")) {
        // Nested multipart - recurse
        this.parseMultipart(partContent, partContentType);
      } else if (partContentType.includes("text/html")) {
        this.body.html = this.decodeContent(partContent, partEncoding);
      } else if (partContentType.includes("text/plain")) {
        this.body.plain = this.decodeContent(partContent, partEncoding);
      }
    }
  }

  decodeContent(content, encoding) {
    if (!encoding) return content;

    encoding = encoding.toLowerCase().trim();

    if (encoding === "quoted-printable") {
      return this.decodeQuotedPrintable(content);
    } else if (encoding === "base64") {
      return this.decodeBase64(content);
    }

    return content;
  }

  decodeQuotedPrintable(str) {
    // First, handle soft line breaks (= at end of line)
    str = str.replace(/=\r?\n/g, "");

    // Convert to bytes, decoding =XX sequences
    // This is needed because =XX codes may represent UTF-8 multi-byte sequences
    // e.g., =C2=A0 is a two-byte UTF-8 sequence for non-breaking space
    const bytes = [];
    for (let i = 0; i < str.length; i++) {
      if (str[i] === "=" && i + 2 < str.length) {
        const hex = str.substring(i + 1, i + 3);
        if (/^[0-9A-Fa-f]{2}$/.test(hex)) {
          bytes.push(parseInt(hex, 16));
          i += 2;
          continue;
        }
      }
      bytes.push(str.charCodeAt(i));
    }

    // Decode UTF-8 bytes properly
    try {
      return new TextDecoder("utf-8").decode(new Uint8Array(bytes));
    } catch (e) {
      // Fallback if UTF-8 decoding fails
      return bytes.map((b) => String.fromCharCode(b)).join("");
    }
  }

  decodeBase64(str) {
    try {
      // Remove line breaks and decode
      const cleaned = str.replace(/\s/g, "");
      return atob(cleaned);
    } catch (e) {
      return str;
    }
  }

  escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  // Getters for common headers
  get from() {
    return this.parseEmailAddress(this.headers["from"]);
  }

  get to() {
    return this.parseEmailAddress(this.headers["to"]);
  }

  get subject() {
    return this.decodeHeaderValue(this.headers["subject"] || "(No Subject)");
  }

  get date() {
    const dateStr = this.headers["date"];
    if (!dateStr) return null;
    
    try {
      return new Date(dateStr);
    } catch {
      return dateStr;
    }
  }

  get formattedDate() {
    const date = this.date;
    if (!date) return "";
    
    if (date instanceof Date) {
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    }
    return date;
  }

  parseEmailAddress(value) {
    if (!value) return { name: null, email: null, display: "" };

    // Try to match "Name" <email@example.com> format first
    const bracketMatch = value.match(/^"?(.+?)"?\s*<([^>]+)>$/);
    if (bracketMatch) {
      const name = bracketMatch[1]?.trim() || null;
      const email = bracketMatch[2]?.trim();
      return {
        name,
        email,
        display: name ? `${name} <${email}>` : email
      };
    }

    // Otherwise, treat the whole thing as just an email address
    const emailOnly = value.trim();
    // Validate it looks like an email
    if (emailOnly.includes("@")) {
      return {
        name: null,
        email: emailOnly,
        display: emailOnly
      };
    }

    return { name: null, email: value, display: value };
  }

  decodeHeaderValue(value) {
    if (!value) return value;

    // Decode RFC 2047 encoded words: =?charset?encoding?text?=
    return value.replace(/=\?([^?]+)\?([BQbq])\?([^?]*)\?=/g, (_, charset, encoding, text) => {
      if (encoding.toUpperCase() === "B") {
        return this.decodeBase64(text);
      } else if (encoding.toUpperCase() === "Q") {
        return this.decodeQuotedPrintable(text.replace(/_/g, " "));
      }
      return text;
    });
  }

  // Get the best body content (prefer HTML for rich display)
  get content() {
    return this.body.html || this.body.plain || "";
  }

  // Get clean body content for display (strips excessive whitespace)
  get cleanContent() {
    let content = this.content;
    
    if (this.body.html) {
      // For HTML, we'll use it directly but clean up some common issues
      content = content
        .replace(/<!--.*?-->/gs, "") // Remove HTML comments
        .trim();
    } else if (this.body.plain) {
      // For plain text, preserve formatting but clean up
      content = content.trim();
    }
    
    return content;
  }

  // Generate formatted HTML for display in Trix editor
  // Note: Trix has a limited allowlist of HTML elements, so we use Trix-compatible tags
  toFormattedHTML() {
    const from = this.from;
    const to = this.to;
    
    // Use blockquote for the email card styling (Trix supports this)
    // and strong/br for formatting
    return `
<blockquote class="email-card">
<strong>${this.escapeHtml(this.subject)}</strong><br>
<strong>From:</strong> ${this.escapeHtml(from.display)}<br>
<strong>To:</strong> ${this.escapeHtml(to.display)}<br>
<strong>Date:</strong> ${this.escapeHtml(this.formattedDate)}<br>
<br>
${this.getBodyForDisplay()}
</blockquote>
`.trim();
  }

  getBodyForDisplay() {
    if (this.body.html) {
      // Extract just the body content from the HTML email
      // For Trix, we need to simplify it to basic text
      return this.extractTextFromHtml(this.body.html);
    } else if (this.body.plain) {
      // Plain text - escape and return
      return this.escapeHtml(this.body.plain);
    }
    return "<em>No content</em>";
  }

  extractTextFromHtml(html) {
    // Extract text content and preserve basic structure
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    const bodyContent = bodyMatch ? bodyMatch[1] : html;
    
    // Create a temporary element to extract text
    if (typeof document !== "undefined") {
      const temp = document.createElement("div");
      temp.innerHTML = bodyContent;
      // Get text, preserving some structure
      return this.escapeHtml(temp.textContent || temp.innerText || "");
    }
    
    // Fallback: strip tags manually
    return this.escapeHtml(bodyContent.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim());
  }

  escapeHtml(str) {
    if (!str) return "";
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  // Simple summary for display
  toSummary() {
    return {
      from: this.from,
      to: this.to,
      subject: this.subject,
      date: this.date,
      formattedDate: this.formattedDate,
      hasHtml: !!this.body.html,
      hasPlain: !!this.body.plain
    };
  }
}

// Helper function for quick parsing
export function parseEmail(rawEmail) {
  return new EmailParser(rawEmail);
}

